// FILE: src/components/Home.js
import React from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import AboutSection from "./AboutSection.jsx";

export default function Home() {
  const navigate = useNavigate();
  const { user, loading, logout } = useAuth();

  /**
   * ✅ Layout shell
   * - Keeps footer pinned naturally
   */
  const Shell = ({ children }) => (
    <div className="min-h-screen flex flex-col bg-gradient-to-br from-slate-50 to-white">
      <main className="flex-1 px-6 py-10">
        <div className="mx-auto w-full max-w-5xl space-y-6">{children}</div>
      </main>

      <footer className="border-t bg-white">
        <div className="mx-auto w-full max-w-5xl px-6 py-4 text-xs text-gray-500">
          © {new Date().getFullYear()} Equip Foundation
        </div>
      </footer>
    </div>
  );

  const handleLogout = async () => {
    try {
      if (typeof logout === "function") await logout();
    } catch (err) {
      console.error("Logout failed:", err);
    } finally {
      navigate("/", { replace: true });
    }
  };

  // Loading state
  if (loading) {
    return (
      <Shell>
        <section className="bg-white rounded-2xl shadow-lg border border-gray-100 p-8 md:p-10">
          <div className="text-sm text-gray-500">Loading…</div>
        </section>
      </Shell>
    );
  }

  // ✅ Same Home page for EVERYONE (logged out + member + admin)
  return (
    <Shell>
      {/* HERO */}
      <section className="bg-white rounded-2xl shadow-lg border border-gray-100 p-8 md:p-10">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
          <div>
            <h1 className="text-3xl font-extrabold text-gray-900">
              Equip Foundation
            </h1>
            <p className="mt-3 text-gray-600">
              Welcome home. Explore events and content, and stay connected with what God is doing.
            </p>
          </div>

          {/* Buttons */}
          <div className="min-w-[220px]">
            {user ? (
              <div className="grid gap-3">
                <button
                  onClick={handleLogout}
                  className="w-full py-3 rounded-lg bg-red-50 text-red-600 border border-red-200 font-medium hover:bg-red-100 transition"
                >
                  Log out
                </button>
              </div>
            ) : (
              <div className="grid gap-3">
                <button
                  onClick={() => navigate("/login")}
                  className="w-full py-3 rounded-lg border border-indigo-500 text-indigo-600 font-medium hover:bg-indigo-50 transition"
                >
                  Log in
                </button>

                <button
                  onClick={() => navigate("/signup")}
                  className="w-full py-3 rounded-lg bg-indigo-600 text-white font-medium hover:bg-indigo-700 transition shadow"
                >
                  Sign up
                </button>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* ABOUT */}
      <AboutSection />

      {/* GET INVOLVED */}
      <section className="bg-white border rounded-2xl p-8 shadow-sm">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Get involved</h2>
          <p className="text-gray-600 mt-2">
            See what’s happening and access teaching resources.
          </p>
        </div>

        <div className="mt-6 grid md:grid-cols-2 gap-4">
          {/* EVENTS CARD */}
          <button
            onClick={() => navigate("/events")}
            className="
              group w-full text-left
              bg-white border rounded-2xl p-6
              cursor-pointer
              transition-all duration-200
              hover:shadow-md hover:-translate-y-0.5
              hover:border-indigo-300
              focus:outline-none focus:ring-2 focus:ring-indigo-500
            "
          >
            <div className="flex items-start justify-between">
              <div>
                <h3 className="font-semibold text-lg text-gray-900 group-hover:text-indigo-600 transition">
                  Events
                </h3>
                <p className="text-gray-600 mt-1">
                  View upcoming events, schedules, and announcements.
                </p>
              </div>
              <span className="text-gray-400 group-hover:text-indigo-500 transition text-xl">
                →
              </span>
            </div>
          </button>

          {/* CONTENT CARD */}
          <button
            onClick={() => navigate("/content")}
            className="
              group w-full text-left
              bg-white border rounded-2xl p-6
              cursor-pointer
              transition-all duration-200
              hover:shadow-md hover:-translate-y-0.5
              hover:border-indigo-300
              focus:outline-none focus:ring-2 focus:ring-indigo-500
            "
          >
            <div className="flex items-start justify-between">
              <div>
                <h3 className="font-semibold text-lg text-gray-900 group-hover:text-indigo-600 transition">
                  Content
                </h3>
                <p className="text-gray-600 mt-1">
                  Sermons, devotionals, teaching series, and resources.
                </p>
              </div>
              <span className="text-gray-400 group-hover:text-indigo-500 transition text-xl">
                →
              </span>
            </div>
          </button>
        </div>
      </section>
    </Shell>
  );
}